import { Icon } from '@iconify/react'
import { Modal } from 'antd'
import classNames from 'classnames'
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Button } from 'common/components/Button/Button'
import { DatePicker } from 'common/components/DatePicker/DatePicker'
import { Input } from 'common/components/Input/Input'
import { BUTTON_MODIFIER, BUTTON_SEVERITY } from 'common/constants/buttonConstants'
import { useSkipConfirmation } from 'features/Procedure/hooks/useSkipConfirmation'
import dayjs, { type Dayjs } from 'dayjs'

import styles from './product-scanner.module.scss'
import {
  PRODUCT_LABEL_FORMAT_HINT,
  areProductLabelListsEqual,
  createEmptyProductLabel,
  hasAnyProductLabelValue,
  parseProductLabels,
  parseScannedProductLabel,
  serializeProductLabel,
  serializeProductLabels,
  type ProductLabelRowFields,
  type ProductLabelValidationErrors,
  validateProductLabel,
} from './product-scanner.utils'
import { ScannerModal, type ScannerModalSubmitValue } from './scanner-modal'

type ProductScannerProps = {
  isOpen: boolean
  defaultProducts?: string[]
  onClose: () => void
  onProductsScanned?: (products: string[]) => void
}

type ProductLabelRow = ProductLabelRowFields & {
  id: string
  savedValue: string | null
}

const EMPTY_PRODUCTS: string[] = []

const createRowId = () => {
  return `product-label-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

const parseUsDateToDayjs = (value: string): Dayjs | null => {
  if (!value) {
    return null
  }

  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!match) {
    return null
  }

  const month = Number(match[1])
  const day = Number(match[2])
  const year = Number(match[3])
  const date = dayjs(new Date(year, month - 1, day))

  if (!date.isValid()) {
    return null
  }

  if (date.month() !== month - 1 || date.date() !== day || date.year() !== year) {
    return null
  }

  return date
}

const rowToFields = (row: ProductLabelRow): ProductLabelRowFields => ({
  lot: row.lot,
  serial: row.serial,
  mfg: row.mfg,
  exp: row.exp,
  gtin: row.gtin,
  tagid: row.tagid,
})

const toSavedValue = (fields: ProductLabelRowFields): string | null => {
  if (!hasAnyProductLabelValue(fields)) {
    return null
  }

  return serializeProductLabel(fields)
}

const isRowSaved = (row: ProductLabelRow): boolean => {
  return row.savedValue !== null && row.savedValue === serializeProductLabel(rowToFields(row))
}

const getCollapsedRowIds = (items: ProductLabelRow[]): string[] => {
  return items.filter((row) => isRowSaved(row)).map((row) => row.id)
}

const getRowSummary = (row: ProductLabelRow): string => {
  const summaryParts = [
    row.lot && `LOT ${row.lot}`,
    row.serial && `SERIAL ${row.serial}`,
    row.exp && `EXP ${row.exp}`,
    row.gtin && `GTIN ${row.gtin}`,
  ].filter(Boolean) as string[]

  return summaryParts.length > 0 ? summaryParts.join(' / ') : 'Empty label'
}

export const ProductScanner = ({
  isOpen,
  defaultProducts = EMPTY_PRODUCTS,
  onClose,
  onProductsScanned,
}: ProductScannerProps) => {
  const { executeWithConfirmation } = useSkipConfirmation()
  const wasOpenRef = useRef(false)

  const [rows, setRows] = useState<ProductLabelRow[]>([])
  const [isScannerOpen, setIsScannerOpen] = useState(false)
  const [initialPayload, setInitialPayload] = useState<string[]>([])
  const [rowErrors, setRowErrors] = useState<Record<string, ProductLabelValidationErrors>>({})
  const [collapsedRowIds, setCollapsedRowIds] = useState<string[]>([])

  const createRow = useCallback(
    (
      fields?: Partial<ProductLabelRowFields>,
      options?: {
        savedValue?: string | null
      },
    ): ProductLabelRow => {
      return {
        id: createRowId(),
        ...createEmptyProductLabel(),
        ...fields,
        savedValue: options?.savedValue ?? null,
      }
    },
    [],
  )

  const getPayloadFromRows = useCallback((items: ProductLabelRow[]) => {
    const nonEmptyRows = items
      .map((row) => rowToFields(row))
      .filter((fields) => hasAnyProductLabelValue(fields))

    return serializeProductLabels(nonEmptyRows)
  }, [])

  const initializeFromDefaultProducts = useCallback(() => {
    const parsedDefaults = parseProductLabels(defaultProducts)
    const nextRows =
      parsedDefaults.length > 0
        ? parsedDefaults.map((fields) =>
            createRow(fields, {
              savedValue: toSavedValue(fields),
            }),
          )
        : [createRow()]

    setRows(nextRows)
    setInitialPayload(serializeProductLabels(parsedDefaults))
    setRowErrors({})
    setCollapsedRowIds(getCollapsedRowIds(nextRows))
  }, [createRow, defaultProducts])

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      initializeFromDefaultProducts()
    }

    if (!isOpen) {
      setIsScannerOpen(false)
      setCollapsedRowIds([])
    }

    wasOpenRef.current = isOpen
  }, [initializeFromDefaultProducts, isOpen])

  const validateRows = useCallback((items: ProductLabelRow[]) => {
    const errorsByRowId: Record<string, ProductLabelValidationErrors> = {}

    items.forEach((row) => {
      const fields = rowToFields(row)
      if (!hasAnyProductLabelValue(fields)) {
        return
      }

      const validation = validateProductLabel(fields)
      if (Object.keys(validation).length > 0) {
        errorsByRowId[row.id] = validation
      }
    })

    return errorsByRowId
  }, [])

  const payload = useMemo(() => getPayloadFromRows(rows), [getPayloadFromRows, rows])

  const isDirty = useMemo(() => {
    return !areProductLabelListsEqual(payload, initialPayload)
  }, [initialPayload, payload])

  const filledProductsCount = useMemo(() => {
    return rows.filter((row) => hasAnyProductLabelValue(rowToFields(row))).length
  }, [rows])

  const savedProductsCount = useMemo(() => {
    return rows.filter((row) => isRowSaved(row)).length
  }, [rows])

  const handleFieldChange = useCallback(
    (rowId: string, field: keyof ProductLabelRowFields, value: string) => {
      setCollapsedRowIds((prev) => prev.filter((id) => id !== rowId))
      setRows((prevRows) =>
        prevRows.map((row) => {
          if (row.id !== rowId) {
            return row
          }

          return {
            ...row,
            [field]: value,
          }
        }),
      )
    },
    [],
  )

  const handleExpChange = useCallback((rowId: string, value: Dayjs | null) => {
    setCollapsedRowIds((prev) => prev.filter((id) => id !== rowId))
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id !== rowId) {
          return row
        }

        return {
          ...row,
          exp: value ? value.endOf('month').format('MM/DD/YYYY') : '',
        }
      }),
    )
  }, [])

  const handleRowBlur = useCallback(
    (rowId: string) => {
      const row = rows.find((item) => item.id === rowId)
      if (!row) {
        return
      }

      const fields = rowToFields(row)

      setRowErrors((prev) => {
        const next = { ...prev }

        if (!hasAnyProductLabelValue(fields)) {
          delete next[rowId]
          return next
        }

        const validation = validateProductLabel(fields)
        if (Object.keys(validation).length > 0) {
          next[rowId] = validation
        } else {
          delete next[rowId]
        }

        return next
      })
    },
    [rows],
  )

  const handleAddRow = useCallback(() => {
    setRows((prevRows) => [...prevRows, createRow()])
  }, [createRow])

  const handleDeleteRow = useCallback(
    (rowId: string) => {
      setRows((prevRows) => {
        const nextRows = prevRows.filter((row) => row.id !== rowId)
        return nextRows.length > 0 ? nextRows : [createRow()]
      })
      setCollapsedRowIds((prev) => prev.filter((id) => id !== rowId))
      setRowErrors((prev) => {
        const next = { ...prev }
        delete next[rowId]
        return next
      })
    },
    [createRow],
  )

  const handleToggleRowCollapse = useCallback((rowId: string) => {
    setCollapsedRowIds((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId],
    )
  }, [])

  const handleCameraScan = useCallback(() => {
    setIsScannerOpen(true)
  }, [])

  const handleScannerClose = useCallback(() => {
    setIsScannerOpen(false)
  }, [])

  const handleScannerSubmit = useCallback(
    (scan: ScannerModalSubmitValue) => {
      const parsed = parseScannedProductLabel(scan.rawValue, scan.format)
      let nextRowId = ''
      let nextFields: ProductLabelRowFields | null = null

      setRows((prevRows) => {
        if (prevRows.length === 1 && !hasAnyProductLabelValue(rowToFields(prevRows[0]))) {
          nextRowId = prevRows[0].id
          nextFields = parsed
          return [{ ...prevRows[0], ...parsed }]
        }

        const nextRow = createRow(parsed)
        nextRowId = nextRow.id
        nextFields = rowToFields(nextRow)
        return [...prevRows, nextRow]
      })

      setRowErrors((prev) => {
        if (!nextRowId || !nextFields) {
          return prev
        }

        const next = { ...prev }

        if (!hasAnyProductLabelValue(nextFields)) {
          delete next[nextRowId]
          return next
        }

        const validation = validateProductLabel(nextFields)
        if (Object.keys(validation).length > 0) {
          next[nextRowId] = validation
        } else {
          delete next[nextRowId]
        }

        return next
      })

      setIsScannerOpen(false)
    },
    [createRow],
  )

  const closeWithoutConfirmation = useCallback(() => {
    setIsScannerOpen(false)
    onClose()
  }, [onClose])

  const handleClose = useCallback(() => {
    if (!isDirty) {
      closeWithoutConfirmation()
      return
    }

    executeWithConfirmation(
      {
        title: 'Discard changes?',
        message: 'You have unsaved product labels. Discard your changes?',
        mainButtonText: 'Discard',
        secondButtonText: 'Keep editing',
      },
      closeWithoutConfirmation,
    )
  }, [closeWithoutConfirmation, executeWithConfirmation, isDirty])

  const handleSave = useCallback(
    (event?: FormEvent) => {
      event?.preventDefault()

      const validationErrors = validateRows(rows)
      setRowErrors(validationErrors)

      if (Object.keys(validationErrors).length > 0) {
        return
      }

      const nextPayload = getPayloadFromRows(rows)
      if (onProductsScanned && !areProductLabelListsEqual(nextPayload, initialPayload)) {
        onProductsScanned(nextPayload)
      }

      setInitialPayload(nextPayload)
      closeWithoutConfirmation()
    },
    [
      closeWithoutConfirmation,
      getPayloadFromRows,
      initialPayload,
      onProductsScanned,
      rows,
      validateRows,
    ],
  )

  return (
    <>
      <Modal
        destroyOnHidden
        title='Scanned Products'
        open={isOpen}
        onCancel={handleClose}
        footer={null}
        width={980}
        classNames={{ content: styles.modalContainer }}>
        <form className={styles.editorContainer} onSubmit={handleSave}>
          <p className={styles.formatHint}>
            {PRODUCT_LABEL_FORMAT_HINT} with empty fields allowed.
          </p>

          <div className={styles.editorSummary}>
            <span>
              {filledProductsCount} product label(s), {savedProductsCount} saved
            </span>
          </div>

          <div className={styles.rowsList}>
            {rows.map((row, index) => {
              const rowSaved = isRowSaved(row)
              const isCollapsed = collapsedRowIds.includes(row.id)

              return (
                <div
                  key={row.id}
                  className={classNames(styles.rowCard, {
                    [styles.rowCardCollapsed]: isCollapsed,
                  })}>
                  <div className={styles.rowHeader}>
                    <div className={styles.rowTitleWrap}>
                      <span
                        className={classNames(styles.rowIndex, {
                          [styles.rowIndexSaved]: rowSaved,
                          [styles.rowIndexWarning]: !rowSaved,
                        })}>
                        #{index + 1}
                      </span>
                      <div className={styles.rowHeading}>
                        <span className={styles.rowTitle}>
                          {rowSaved ? 'Saved label' : 'Unsaved label'}
                        </span>
                        {isCollapsed && (
                          <span className={styles.rowSummary}>{getRowSummary(row)}</span>
                        )}
                      </div>
                    </div>
                    <div className={styles.rowHeaderActions}>
                      <button
                        type='button'
                        className={styles.toggleRowButton}
                        onClick={() => handleToggleRowCollapse(row.id)}
                        aria-label={isCollapsed ? 'Expand product row' : 'Collapse product row'}>
                        <Icon
                          icon={isCollapsed ? 'mdi:chevron-down' : 'mdi:chevron-up'}
                          width={20}
                          height={20}
                        />
                      </button>
                      <button
                        type='button'
                        className={styles.deleteRowButton}
                        onClick={() => handleDeleteRow(row.id)}
                        aria-label='Delete product row'>
                        <Icon icon='mdi:trash-can-outline' width={20} height={20} />
                      </button>
                    </div>
                  </div>

                  {!isCollapsed && (
                    <div className={styles.fieldsGrid}>
                      <Input
                        name={`rows.${row.id}.lot`}
                        required
                        label='LOT'
                        value={row.lot}
                        placeholder='LOT'
                        error={rowErrors[row.id]?.lot}
                        onChange={(event) => handleFieldChange(row.id, 'lot', event.target.value)}
                        onBlur={() => handleRowBlur(row.id)}
                      />
                      <Input
                        name={`rows.${row.id}.serial`}
                        label='SERIAL'
                        value={row.serial}
                        placeholder='SERIAL'
                        error={rowErrors[row.id]?.serial}
                        onChange={(event) =>
                          handleFieldChange(row.id, 'serial', event.target.value)
                        }
                        onBlur={() => handleRowBlur(row.id)}
                      />
                      <Input
                        name={`rows.${row.id}.mfg`}
                        label='MFG'
                        value={row.mfg}
                        placeholder='MM/DD/YYYY'
                        error={rowErrors[row.id]?.mfg}
                        onChange={(event) => handleFieldChange(row.id, 'mfg', event.target.value)}
                        onBlur={() => handleRowBlur(row.id)}
                      />
                      <DatePicker
                        required
                        label='EXP'
                        picker='month'
                        format='MM/YYYY'
                        value={parseUsDateToDayjs(row.exp)}
                        placeholder='MM/YYYY'
                        error={rowErrors[row.id]?.exp}
                        onChange={(date) => handleExpChange(row.id, date)}
                        onBlur={() => handleRowBlur(row.id)}
                      />
                      <Input
                        name={`rows.${row.id}.gtin`}
                        label='GTIN'
                        value={row.gtin}
                        placeholder='GTIN'
                        error={rowErrors[row.id]?.gtin}
                        onChange={(event) => handleFieldChange(row.id, 'gtin', event.target.value)}
                        onBlur={() => handleRowBlur(row.id)}
                      />
                      <Input
                        name={`rows.${row.id}.tagid`}
                        label='TAGID (9d)'
                        value={row.tagid}
                        placeholder='000000000'
                        maxLength={9}
                        error={rowErrors[row.id]?.tagid}
                        onChange={(event) => handleFieldChange(row.id, 'tagid', event.target.value)}
                        onBlur={() => handleRowBlur(row.id)}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <div className={styles.modalActions}>
            <div className={styles.modalActionsGroup}>
              <Button
                htmlType='button'
                modifier={BUTTON_MODIFIER.SECONDARY}
                icon={<Icon icon='mdi:plus' width={20} height={20} />}
                onClick={handleAddRow}
                className={styles.actionButton}>
                Add Product
              </Button>
              <Button
                htmlType='button'
                severity={BUTTON_SEVERITY.SUCCESS_FILLED}
                icon={<Icon icon='fluent:scan-camera-48-filled' width={20} height={20} />}
                onClick={handleCameraScan}
                className={styles.actionButton}>
                Scan
              </Button>
            </div>
            <div className={styles.modalActionsGroup}>
              <Button htmlType='button' modifier={BUTTON_MODIFIER.SECONDARY} onClick={handleClose}>
                Cancel
              </Button>
              <Button htmlType='submit'>Save</Button>
            </div>
          </div>
        </form>
      </Modal>

      <ScannerModal
        isOpen={isScannerOpen}
        onClose={handleScannerClose}
        onSubmit={handleScannerSubmit}
      />
    </>
  )
}
